import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Product, Category } from '../types';
import ProductCard  from '../components/ProductCard';
import ProductModal from '../components/ProductModal';
import logo from '../assets/logo-somzita.png';

export default function HomePage() {
  const [products,   setProducts]   = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [active,     setActive]     = useState<number | null>(null);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(true);
  const [preview,    setPreview]    = useState<Product | null>(null);

  useEffect(() => {
    async function load() {
      const [{ data: prods }, { data: cats }] = await Promise.all([
        supabase.from('products').select('*, categories(id,name,slug)').eq('is_active', true).order('name'),
        supabase.from('categories').select('*').order('name'),
      ]);
      setProducts(prods || []);
      setCategories(cats || []);
      setLoading(false);
    }
    load();
  }, []);

  const filtered = products.filter(p => {
    const matchCat    = active ? p.category_id === active : true;
    const q           = search.toLowerCase();
    const matchSearch = q
      ? p.name.toLowerCase().includes(q) ||
        (p.short_description || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      : true;
    return matchCat && matchSearch;
  });

  const filterBtn = (isActive: boolean): React.CSSProperties => ({
    height: '36px', padding: '0 1rem',
    background: isActive ? 'rgba(168,196,162,0.12)' : 'none',
    border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
    color: isActive ? 'var(--accent)' : 'var(--muted)',
    borderRadius: '2rem', fontSize: '0.78rem',
    whiteSpace: 'nowrap', transition: 'all 0.2s', flexShrink: 0,
  });

  return (
    <main>
      {/* ── Hero compact mobile ── */}
      <section style={{
        padding: '2rem var(--page-px) 1.5rem',
        textAlign: 'center', position: 'relative',
      }}>
        <div style={{
          position:'absolute', top:0, left:'50%', transform:'translateX(-50%)',
          width:'min(500px,100%)', height:'200px',
          background:'radial-gradient(ellipse, rgba(168,196,162,0.07) 0%, transparent 70%)',
          pointerEvents:'none',
        }} />

        <div style={{
          display:'inline-block', fontSize:'0.62rem', letterSpacing:'0.2em',
          textTransform:'uppercase', color:'var(--accent)',
          border:'1px solid rgba(168,196,162,0.25)',
          padding:'0.25rem 0.85rem', borderRadius:'2rem', marginBottom:'1.1rem',
        }}>
          Livraison discrète · Ouagadougou
        </div>

        <div style={{ display:'flex', justifyContent:'center', width:'100%', marginBottom:'0.75rem' }}>
          <img
            src={logo}
            alt="Somzita"
            style={{ height:'clamp(48px,12vw,80px)', width:'auto', objectFit:'contain' }}
          />
        </div>
        <h1 style={{
          fontFamily:"'Cormorant Garamond',serif",
          fontSize:'clamp(1.75rem,7vw,3.5rem)',
          fontWeight:300, lineHeight:1.2, marginBottom:'0.75rem',
        }}>
          Ce dont vous avez besoin,<br />
          <em style={{ fontStyle:'italic', color:'var(--accent)' }}>livré sans regards.</em>
        </h1>

        <p style={{ color:'var(--muted)', fontSize:'0.82rem', maxWidth:'360px', margin:'0 auto', lineHeight:1.7 }}>
          Emballage neutre, livraison rapide. Personne ne sait ce que vous avez commandé.
        </p>
      </section>

      {/* ── Search ── */}
      <div style={{ padding:'0 var(--page-px) 0.75rem' }}>
        <div style={{ position:'relative' }}>
          <svg style={{ position:'absolute', left:'0.9rem', top:'50%', transform:'translateY(-50%)', color:'var(--muted)', pointerEvents:'none' }}
            width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="search"
            placeholder="Rechercher…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width:'100%', height:'44px',
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'2rem',
              color:'var(--text)',
              padding:'0 2.5rem 0 2.5rem',
              fontFamily:'Outfit,sans-serif', fontSize:'16px',
              outline:'none', WebkitAppearance:'none',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{
              position:'absolute', right:'0.7rem', top:'50%', transform:'translateY(-50%)',
              background:'none', border:'none', color:'var(--muted)',
              width:'2rem', height:'2rem', display:'flex', alignItems:'center', justifyContent:'center',
            }}>✕</button>
          )}
        </div>
      </div>

      {/* ── Filters scrollable horizontalement ── */}
      <div style={{
        display:'flex', gap:'0.4rem',
        padding:'0 var(--page-px) 1.2rem',
        overflowX:'auto', WebkitOverflowScrolling:'touch' as any,
        scrollbarWidth:'none', msOverflowStyle:'none',
      }}>
        <style>{`.filters-scroll::-webkit-scrollbar{display:none}`}</style>
        <button onClick={() => setActive(null)} style={filterBtn(active === null)}>Tout</button>
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setActive(cat.id)} style={filterBtn(active === cat.id)}>
            {cat.name}
          </button>
        ))}
      </div>

      {/* ── Products ── */}
      <section style={{ padding:'0 var(--page-px) 5rem' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:'3rem', color:'var(--muted)', fontSize:'0.82rem' }}>Chargement…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:'3rem', color:'var(--muted)' }}>
            <div style={{ fontSize:'2rem', marginBottom:'0.5rem' }}>🔍</div>
            <p style={{ fontSize:'0.85rem' }}>Aucun produit{search ? ` pour "${search}"` : ''}.</p>
          </div>
        ) : (
          <div style={{
            display:'grid',
            /* 1 colonne sur très petit, 2 colonnes dès 360px, 3+ sur desktop */
            gridTemplateColumns:'repeat(auto-fill, minmax(min(100%, 160px), 1fr))',
            gap:'0.75rem',
          }}>
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onPreview={setPreview} />
            ))}
          </div>
        )}
      </section>

      <footer style={{
        textAlign:'center', padding:'1.5rem var(--page-px)',
        paddingBottom:'calc(1.5rem + env(safe-area-inset-bottom))',
        borderTop:'1px solid var(--border)',
        color:'var(--muted)', fontSize:'0.7rem', letterSpacing:'0.06em',
      }}>
        © 2026 Somzita — Discrétion garantie · Ouagadougou
      </footer>

      <ProductModal product={preview} onClose={() => setPreview(null)} />
    </main>
  );
}
